/**
 * Relation slots that accept a BASE TABLE only — never a view.
 *
 * An index belongs to a table: `CREATE INDEX ix ON ▮`, `DROP INDEX ix ON ▮`
 * and MySQL's `SHOW INDEX FROM ▮` all reject a view outright (MySQL: "'v' is
 * not BASE TABLE"; PG/Oracle/MSSQL raise the equivalent). The resolvers emit
 * their whole relation list at those carets, so the views sit in the widget as
 * suggestions that cannot work — mysql coverage script.31 §2/§4 and 36 §5 pin
 * exactly that leak (`customer_list`, `film_list`).
 *
 * A post-filter rather than a slot, for the same reason as
 * `parenScopeFilter.ts`: the tables the resolver already emitted ARE the right
 * answer here, and only the views have to go. Being a post-filter also makes
 * it independent of which arm produced them — a `CREATE INDEX … ON ▮` caret
 * classifies as `unknown` while `SHOW INDEX FROM ▮` classifies as `from`.
 *
 * Cross-engine: every dialect spells `CREATE [<type>] INDEX <name> ON <table>`
 * and rejects a view there, so the recognizer is shared and wired into all
 * five resolvers. `SHOW INDEX|INDEXES|KEYS FROM` is MySQL-only, and
 * `DROP INDEX … ON <table>` is MySQL / T-SQL — both are recognised by shape,
 * so an engine whose grammar lacks them simply never matches.
 *
 * Materialized views are deliberately NOT covered: PG and Oracle both index a
 * materialized view legally, and the snapshot marks them with their own kind
 * (`materialized-view`), so only plain `'view'` candidates are dropped.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { dropAttachedPartialTokens, tokenize, type Token } from '../context/tokenizer';

export function applyIndexTargetFilter(
  candidates: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): CompletionCandidate[] {
  if (!isAtIndexTargetRelationSlot(text, offset, statementStart, engine)) return candidates;
  return candidates.filter((c) => c.kind !== 'view');
}

/**
 * True when the caret names the table an index lives on. A partially typed
 * name still counts — the filter must hold while the user types, or the views
 * reappear on the first keystroke.
 */
export function isAtIndexTargetRelationSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  const tokens = dropAttachedPartialTokens(
    tokenize(text, statementStart, offset, engine),
    offset,
  );
  const tail = wordOf(tokens[tokens.length - 1]);
  if (tail === null) return false;
  const words = tokens.map((t) => wordOf(t)).filter((w): w is string => w !== null);

  // `CREATE [UNIQUE|FULLTEXT|…] INDEX <name> ON ▮` / `DROP INDEX <name> ON ▮`.
  if (tail === 'ON') {
    return words[0] === 'CREATE' || words[0] === 'DROP'
      ? words.includes('INDEX')
      : false;
  }
  // MySQL's `SHOW {INDEX|INDEXES|KEYS} {FROM|IN} ▮`.
  if (tail === 'FROM' || tail === 'IN') {
    return (
      words[0] === 'SHOW' &&
      (words[1] === 'INDEX' || words[1] === 'INDEXES' || words[1] === 'KEYS')
    );
  }
  return false;
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
